import React, { useEffect, useRef, useState } from 'react';
import { Field, Form as FinalForm } from 'react-final-form';
import arrayMutators from 'final-form-arrays';
import classNames from 'classnames';
import { useLocation } from 'react-router-dom';

import { useIntl } from '../../../util/reactIntl';
import * as validators from '../../../util/validators';
import { getPropsForCustomUserFieldInputs } from '../../../util/userHelpers';
import { parse } from '../../../util/urlHelpers';
import {
  Form,
  PrimaryButton,
  FieldTextInput,
  CustomExtendedDataField,
  NamedLink,
} from '../../../components';
import UserFieldDisplayName from '../UserFieldDisplayName';
import UserFieldPhoneNumber from '../UserFieldPhoneNumber';
import SignupLayout, { isAdvertiser } from './SignupLayout';
import SignupIcon from './SignupIcon';
import css from './SignupForm.module.css';

const profileCopy = config =>
  isAdvertiser(config)
    ? {
        title: 'Je suis annonceur',
        description: (
          <>
            Pour proposer mes espaces,
            <br />à la location ou à la vente.
          </>
        ),
        icon: 'building',
      }
    : config.userType === 'customer' || config.roles?.customer
    ? {
        title: 'Je cherche un espace',
        description: (
          <>
            Pour mon équipe ou mon activité,
            <br />à louer ou à acheter.
          </>
        ),
        icon: 'search',
      }
    : { title: config.label, description: '', icon: 'search' };

const SignupFields = props => {
  const {
    rootClassName,
    className,
    formId = 'SignupForm',
    handleSubmit,
    inProgress,
    submitting,
    invalid,
    intl,
    termsAndConditions,
    preselectedUserType,
    userTypes,
    userFields,
    values,
    errorMessage,
    from,
    renderSocialLogins,
  } = props;
  const { userType } = values;
  const userTypeConfig = userTypes.find(config => config.userType === userType);
  const canChooseProfile = !preselectedUserType && userTypes.length > 1;
  const [step, setStep] = useState(userType || userTypes.length < 2 ? 'details' : 'project');
  const [roleError, setRoleError] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const headingRef = useRef(null);
  const roleRef = useRef(null);
  const pending = inProgress || submitting;
  const isProject = step === 'project';
  const hasValidRole = userTypes.length === 0 || !!userTypeConfig;
  const previousStep = useRef(step);

  useEffect(() => {
    if (previousStep.current !== step) {
      headingRef.current?.focus();
      headingRef.current?.scrollIntoView?.({ block: 'nearest' });
      previousStep.current = step;
    }
  }, [step]);

  const moveTo = next => {
    setStep(next);
    setShowPassword(false);
  };
  const continueToDetails = () => {
    if (!hasValidRole) {
      setRoleError(true);
      roleRef.current?.focus();
      return;
    }
    setRoleError(false);
    moveTo('details');
  };
  const onSubmit = event => {
    if (pending) {
      event.preventDefault();
      return;
    }
    if (isProject) {
      event.preventDefault();
      continueToDetails();
      return;
    }
    return handleSubmit(event);
  };
  const required = id => validators.required(intl.formatMessage({ id }));
  const passwordValidators = validators.composeValidators(
    validators.requiredStringNoTrim(intl.formatMessage({ id: 'SignupForm.passwordRequired' })),
    validators.minLength(
      intl.formatMessage(
        { id: 'SignupForm.passwordTooShort' },
        { minLength: validators.PASSWORD_MIN_LENGTH }
      ),
      validators.PASSWORD_MIN_LENGTH
    ),
    validators.maxLength(
      intl.formatMessage(
        { id: 'SignupForm.passwordTooLong' },
        { maxLength: validators.PASSWORD_MAX_LENGTH }
      ),
      validators.PASSWORD_MAX_LENGTH
    )
  );
  const userFieldProps = getPropsForCustomUserFieldInputs(userFields, intl, userType);
  const loginState = { state: { ...(from ? { from } : {}), ...(userType ? { userType } : {}) } };

  return (
    <SignupLayout userTypeConfig={userTypeConfig}>
      <ol
        className={classNames(css.steps, { [css.detailsSteps]: !isProject })}
        aria-label="Étapes de l’inscription"
      >
        <li
          className={isProject ? css.current : css.done}
          aria-current={isProject ? 'step' : undefined}
        >
          <span className={css.stepNumber}>1</span>
          <span>Votre projet</span>
        </li>
        <li className={css.stepLine} aria-hidden="true" />
        <li
          className={!isProject ? css.current : undefined}
          aria-current={!isProject ? 'step' : undefined}
        >
          <span className={css.stepNumber}>2</span>
          <span>Vos informations</span>
        </li>
      </ol>
      {!isProject && canChooseProfile ? (
        <button
          className={css.backStep}
          type="button"
          disabled={pending}
          onClick={() => moveTo('project')}
        >
          <SignupIcon name="arrow" />
          Modifier mon choix
        </button>
      ) : null}
      <p className={css.eyebrow}>
        {isProject
          ? 'BIENVENUE CHEZ COBUCO'
          : isAdvertiser(userTypeConfig)
          ? 'VOTRE COMPTE ANNONCEUR'
          : 'VOTRE COMPTE COBUCO'}
      </p>
      <h1
        id="signup-heading"
        className={classNames(css.heading, { [css.detailsHeading]: !isProject })}
        ref={headingRef}
        tabIndex={-1}
      >
        {isProject ? (
          <>
            Faisons place
            <br />à votre projet.
          </>
        ) : (
          'On fait connaissance ?'
        )}
      </h1>
      <p className={css.intro}>
        {isProject ? (
          <>
            Un compte, selon vos envies.
            <br />
            Comment souhaitez-vous utiliser Cobuco ?
          </>
        ) : (
          'Quelques informations pour créer votre compte.'
        )}
      </p>
      <Form
        className={classNames(rootClassName || css.root, className)}
        onSubmit={onSubmit}
        aria-busy={pending}
      >
        <Field
          name="userType"
          validate={value =>
            userTypes.length === 0 || userTypes.some(c => c.userType === value)
              ? undefined
              : 'Choisissez le profil qui correspond à votre projet.'
          }
        >
          {({ input }) =>
            isProject ? (
              <fieldset
                className={css.roleOptions}
                aria-describedby={roleError ? 'signup-role-error' : undefined}
              >
                <legend className={css.srOnly}>Choisissez votre profil</legend>
                {userTypes.map((config, index) => {
                  const copy = profileCopy(config);
                  return (
                    <label
                      className={classNames(css.roleCard, {
                        [css.roleSelected]: input.value === config.userType,
                      })}
                      key={config.userType}
                    >
                      <input
                        {...input}
                        ref={index === 0 ? roleRef : undefined}
                        type="radio"
                        value={config.userType}
                        checked={input.value === config.userType}
                        onChange={() => {
                          input.onChange(config.userType);
                          setRoleError(false);
                        }}
                        disabled={pending}
                      />
                      <span className={css.roleIcon}>
                        <SignupIcon name={copy.icon} />
                      </span>
                      <span className={css.roleText}>
                        <strong>{copy.title}</strong>
                        <span>{copy.description}</span>
                      </span>
                      <span className={css.radioMark}>
                        <SignupIcon name="check" />
                      </span>
                    </label>
                  );
                })}
              </fieldset>
            ) : (
              <input name={input.name} type="hidden" value={input.value || ''} />
            )
          }
        </Field>
        {isProject ? (
          <>
            {roleError ? (
              <p className={css.error} id="signup-role-error" role="alert">
                Choisissez le profil qui correspond à votre projet.
              </p>
            ) : null}
            <PrimaryButton className={css.primary} type="submit" disabled={pending}>
              Continuer <SignupIcon name="arrow" />
            </PrimaryButton>
          </>
        ) : (
          <>
            <div className={css.defaultUserFields}>
              <div className={css.name}>
                <FieldTextInput
                  type="text"
                  id={`${formId}.fname`}
                  name="fname"
                  autoComplete="given-name"
                  label={intl.formatMessage({ id: 'SignupForm.firstNameLabel' })}
                  placeholder="Votre prénom"
                  validate={required('SignupForm.firstNameRequired')}
                  disabled={pending}
                />
                <FieldTextInput
                  type="text"
                  id={`${formId}.lname`}
                  name="lname"
                  autoComplete="family-name"
                  label={intl.formatMessage({ id: 'SignupForm.lastNameLabel' })}
                  placeholder="Votre nom"
                  validate={required('SignupForm.lastNameRequired')}
                  disabled={pending}
                />
              </div>
              <UserFieldDisplayName
                formName="SignupForm"
                formId={formId}
                className={css.row}
                userTypeConfig={userTypeConfig}
                intl={intl}
                disabled={pending}
                label={isAdvertiser(userTypeConfig) ? 'Nom de votre entreprise' : undefined}
                placeholder={
                  isAdvertiser(userTypeConfig)
                    ? 'Le nom de votre entreprise'
                    : 'Votre nom ou celui de votre entreprise'
                }
                autoComplete={isAdvertiser(userTypeConfig) ? 'organization' : 'nickname'}
              />
              <FieldTextInput
                type="email"
                id={`${formId}.email`}
                name="email"
                autoComplete="email"
                label={intl.formatMessage({ id: 'SignupForm.emailLabel' })}
                placeholder="vous@entreprise.fr"
                validate={validators.composeValidators(
                  required('SignupForm.emailRequired'),
                  validators.emailFormatValid(intl.formatMessage({ id: 'SignupForm.emailInvalid' }))
                )}
                disabled={pending}
              />
              <div className={css.passwordField}>
                <FieldTextInput
                  type={showPassword ? 'text' : 'password'}
                  id={`${formId}.password`}
                  name="password"
                  autoComplete="new-password"
                  label={intl.formatMessage({ id: 'SignupForm.passwordLabel' })}
                  placeholder={`${validators.PASSWORD_MIN_LENGTH} caractères minimum`}
                  validate={passwordValidators}
                  aria-describedby={`${formId}.password-hint`}
                  disabled={pending}
                />
                <button
                  className={css.passwordToggle}
                  type="button"
                  aria-label={showPassword ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
                  aria-pressed={showPassword}
                  disabled={pending}
                  onClick={() => setShowPassword(!showPassword)}
                >
                  <SignupIcon name="eye" />
                </button>
                <p className={css.fieldHint} id={`${formId}.password-hint`}>
                  Utilisez au moins {validators.PASSWORD_MIN_LENGTH} caractères.
                </p>
              </div>
              <UserFieldPhoneNumber
                formName="SignupForm"
                formId={formId}
                className={css.row}
                userTypeConfig={userTypeConfig}
                intl={intl}
                disabled={pending}
              />
            </div>
            {userFieldProps.length ? (
              <div className={css.customFields}>
                {userFieldProps.map(({ key, ...fieldProps }) => (
                  <CustomExtendedDataField key={key} {...fieldProps} formId={formId} />
                ))}
              </div>
            ) : null}
            <div className={css.bottomWrapper}>
              <div className={css.terms}>{termsAndConditions}</div>
              {errorMessage ? (
                <div className={css.error} role="alert">
                  {errorMessage}
                </div>
              ) : null}
              <PrimaryButton
                className={css.primary}
                type="submit"
                inProgress={pending}
                disabled={invalid || !hasValidRole || pending}
              >
                Créer mon compte <SignupIcon name="arrow" />
              </PrimaryButton>
            </div>
          </>
        )}
      </Form>
      {!isProject && renderSocialLogins ? renderSocialLogins(userType) : null}
      <p className={css.loginPrompt}>
        Vous avez déjà un compte ?{' '}
        <NamedLink name="LoginPage" to={loginState}>
          Se connecter
        </NamedLink>
      </p>
    </SignupLayout>
  );
};

const SignupForm = props => {
  const intl = useIntl();
  const location = useLocation();
  const { userTypes = [], userFields = [] } = props;
  const queryType = parse(location.search).userType;
  const preferredType = props.preselectedUserType || queryType;
  const initialType =
    userTypes.find(config => config.userType === preferredType)?.userType ||
    (userTypes.length === 1 ? userTypes[0].userType : undefined);
  return (
    <FinalForm
      key={initialType || 'choose-profile'}
      {...props}
      initialValues={{ userType: initialType }}
      mutators={{ ...arrayMutators }}
      destroyOnUnregister={false}
      render={formProps => (
        <SignupFields {...formProps} intl={intl} userTypes={userTypes} userFields={userFields} />
      )}
    />
  );
};

export default SignupForm;
